import React, { useCallback, useEffect, useState } from 'react';
import { FiCopy, FiMail, FiPlus, FiSlash, FiUserCheck, FiX } from 'react-icons/fi';
import {
  clientPortalService,
  type ClientPortalContext,
  type ClientPortalScope,
  type ClientPortalTeamMember,
} from '../../services/clientPortalService';
import { useI18n } from '../../i18n';
import { formatDate, formatRelative } from './clientPortalFormat';
import './ClientPortal.css';

interface Props {
  context: ClientPortalContext;
  onChanged: () => Promise<unknown>;
}

/**
 * What a primary contact may delegate. Deliberately a subset of the full scope
 * list: `team` stays with the primary, and anything the agency has switched off
 * is filtered out below rather than offered and silently ignored.
 */
const GRANTABLE: Array<{ scope: ClientPortalScope; label: string; hint: string }> = [
  { scope: 'live_map', label: 'Live GPS map', hint: 'See officer positions in real time' },
  { scope: 'active_guards', label: 'On-duty officers', hint: 'Who is clocked in right now' },
  { scope: 'patrol_progress', label: 'Live patrol progress', hint: 'Checkpoints as they are hit' },
  { scope: 'incidents', label: 'Incident reports', hint: 'Incident submissions and photos' },
  { scope: 'daily_reports', label: 'Daily activity reports', hint: 'Shift summaries and observations' },
  { scope: 'patrol_records', label: 'Patrol records', hint: 'Historical tour completion' },
  { scope: 'visitor_logs', label: 'Visitor & vehicle logs', hint: 'Access control records' },
  { scope: 'post_orders', label: 'Post orders', hint: 'Site rules and procedures' },
  { scope: 'schedule', label: 'Coverage calendar', hint: 'Booked shifts for your sites' },
  { scope: 'service_requests', label: 'Cover requests', hint: 'Raise and track extra cover' },
  { scope: 'exports', label: 'Downloads', hint: 'Export reports to PDF and Word' },
];

const PRESETS: Record<string, ClientPortalScope[]> = {
  property_manager: ['incidents', 'daily_reports', 'patrol_records', 'visitor_logs', 'post_orders', 'schedule', 'service_requests', 'exports'],
  front_desk: ['visitor_logs', 'active_guards'],
  security_lead: ['live_map', 'active_guards', 'patrol_progress', 'incidents', 'daily_reports', 'patrol_records', 'visitor_logs', 'post_orders', 'schedule', 'service_requests', 'exports'],
};

export default function ClientTeamPage({ context, onChanged }: Props) {
  const { t } = useI18n();
  const [members, setMembers] = useState<ClientPortalTeamMember[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isBusy, setIsBusy] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [inviteLink, setInviteLink] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);

  const [form, setForm] = useState({
    email: '',
    fullName: '',
    jobTitle: '',
    permissions: [] as ClientPortalScope[],
    siteScope: [] as string[],
    allSites: true,
  });

  // Only offer what the agency has actually enabled for this contract.
  const grantable = GRANTABLE.filter((item) => context.scopes.includes(item.scope));

  const load = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      setMembers(await clientPortalService.fetchTeam());
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!toast) return undefined;
    const timer = window.setTimeout(() => setToast(''), 4000);
    return () => window.clearTimeout(timer);
  }, [toast]);

  const togglePermission = (scope: ClientPortalScope) => {
    setForm((prev) => ({
      ...prev,
      permissions: prev.permissions.includes(scope)
        ? prev.permissions.filter((item) => item !== scope)
        : [...prev.permissions, scope],
    }));
  };

  const applyPreset = (preset: keyof typeof PRESETS) => {
    setForm((prev) => ({
      ...prev,
      permissions: PRESETS[preset].filter((scope) => context.scopes.includes(scope)),
    }));
  };

  const submitInvite = async (event: React.FormEvent) => {
    event.preventDefault();
    setIsBusy(true);
    setError('');
    try {
      const result = await clientPortalService.inviteMember({
        email: form.email.trim(),
        fullName: form.fullName.trim() || null,
        jobTitle: form.jobTitle.trim() || null,
        permissions: form.permissions,
        siteScope: form.allSites ? null : form.siteScope,
      });
      const base = process.env.REACT_APP_MARKETING_URL ?? window.location.origin;
      setInviteLink(`${base.replace(/\/$/, '')}/accept-invite?token=${result.inviteToken}&type=client`);
      setToast(t('Invite created for {email}.', { email: result.email }));
      setIsFormOpen(false);
      setForm({ email: '', fullName: '', jobTitle: '', permissions: [], siteScope: [], allSites: true });
      await load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setIsBusy(false);
    }
  };

  const savePermissions = async (member: ClientPortalTeamMember, permissions: ClientPortalScope[]) => {
    if (!member.id) return;
    setIsBusy(true);
    try {
      await clientPortalService.updateMember({ userId: member.id, permissions });
      await load();
      await onChanged();
      setToast(t('Access updated.'));
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setIsBusy(false);
    }
  };

  const setStatus = async (member: ClientPortalTeamMember, status: 'active' | 'disabled') => {
    if (!member.id) return;
    setIsBusy(true);
    try {
      await clientPortalService.updateMember({ userId: member.id, status });
      await load();
      setToast(status === 'disabled' ? t('Access revoked.') : t('Access restored.'));
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setIsBusy(false);
    }
  };

  const cancelInvite = async (inviteId: string) => {
    setIsBusy(true);
    try {
      await clientPortalService.cancelMemberInvite(inviteId);
      await load();
      setToast(t('Invite cancelled.'));
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setIsBusy(false);
    }
  };

  if (isLoading) {
    return <div className="cp-loading"><div className="spinner" /></div>;
  }

  return (
    <div className="cp-page">
      {error ? <div className="cp-banner cp-banner--error">{error}</div> : null}
      {toast ? <div className="cp-banner cp-banner--success">{toast}</div> : null}

      {inviteLink ? (
        <div className="cp-banner cp-banner--info">
          <span>{t('Share this link if the email does not arrive:')}</span>
          <code className="cp-invite-link">{inviteLink}</code>
          <button
            type="button"
            className="btn btn--secondary btn--sm"
            onClick={() => { void navigator.clipboard?.writeText(inviteLink); setToast(t('Link copied.')); }}
          >
            <FiCopy aria-hidden="true" /> {t('Copy')}
          </button>
          <button type="button" className="cp-icon-btn" aria-label={t('Dismiss')} onClick={() => setInviteLink('')}>
            <FiX aria-hidden="true" />
          </button>
        </div>
      ) : null}

      <article className="cp-panel">
        <header className="cp-panel__head">
          <div>
            <h2>{t('Portal users')}</h2>
            <p>{t('Give colleagues their own sign-in with only the access they need.')}</p>
          </div>
          <button
            type="button"
            className="btn btn--primary btn--sm"
            onClick={() => setIsFormOpen((open) => !open)}
          >
            {isFormOpen ? <FiX aria-hidden="true" /> : <FiPlus aria-hidden="true" />}
            {isFormOpen ? t('Cancel') : t('Invite user')}
          </button>
        </header>

        {isFormOpen ? (
          <form className="cp-form" onSubmit={submitInvite}>
            <label className="cp-field">
              <span>{t('Email')}</span>
              <input
                type="email"
                required
                className="form__input"
                value={form.email}
                onChange={(event) => setForm({ ...form, email: event.target.value })}
              />
            </label>
            <label className="cp-field">
              <span>{t('Name')}</span>
              <input
                type="text"
                className="form__input"
                value={form.fullName}
                onChange={(event) => setForm({ ...form, fullName: event.target.value })}
              />
            </label>
            <label className="cp-field">
              <span>{t('Job title')}</span>
              <input
                type="text"
                className="form__input"
                placeholder={t('e.g. Front desk manager')}
                value={form.jobTitle}
                onChange={(event) => setForm({ ...form, jobTitle: event.target.value })}
              />
            </label>

            <fieldset className="cp-fieldset cp-field--wide">
              <legend>{t('What can they see?')}</legend>
              <div className="cp-presets">
                <span>{t('Start from')}</span>
                <button type="button" className="cp-link" onClick={() => applyPreset('property_manager')}>
                  {t('Property manager')}
                </button>
                <button type="button" className="cp-link" onClick={() => applyPreset('front_desk')}>
                  {t('Front desk')}
                </button>
                <button type="button" className="cp-link" onClick={() => applyPreset('security_lead')}>
                  {t('Security lead')}
                </button>
              </div>

              <div className="cp-perm-grid">
                {grantable.map((item) => (
                  <label key={item.scope} className="cp-perm">
                    <input
                      type="checkbox"
                      checked={form.permissions.includes(item.scope)}
                      onChange={() => togglePermission(item.scope)}
                    />
                    <span>
                      <strong>{t(item.label)}</strong>
                      <small>{t(item.hint)}</small>
                    </span>
                  </label>
                ))}
              </div>
            </fieldset>

            {context.sites.length > 1 ? (
              <fieldset className="cp-fieldset cp-field--wide">
                <legend>{t('Which sites?')}</legend>
                <label className="cp-perm">
                  <input
                    type="checkbox"
                    checked={form.allSites}
                    onChange={(event) => setForm({ ...form, allSites: event.target.checked })}
                  />
                  <span><strong>{t('All sites on the contract')}</strong></span>
                </label>
                {!form.allSites ? (
                  <div className="cp-perm-grid">
                    {context.sites.map((site) => (
                      <label key={site.id} className="cp-perm">
                        <input
                          type="checkbox"
                          checked={form.siteScope.includes(site.id)}
                          onChange={() => setForm((prev) => ({
                            ...prev,
                            siteScope: prev.siteScope.includes(site.id)
                              ? prev.siteScope.filter((id) => id !== site.id)
                              : [...prev.siteScope, site.id],
                          }))}
                        />
                        <span><strong>{site.name}</strong></span>
                      </label>
                    ))}
                  </div>
                ) : null}
              </fieldset>
            ) : null}

            <div className="cp-form__actions">
              <button type="submit" className="btn btn--primary" disabled={isBusy}>
                <FiMail aria-hidden="true" /> {isBusy ? t('Sending…') : t('Send invite')}
              </button>
            </div>
          </form>
        ) : null}

        <ul className="cp-list">
          {members.map((member) => {
            const key = member.id ?? member.invite_id ?? member.email;
            const isEditing = editingId === key;
            return (
              <li key={key} className="cp-list__row cp-list__row--stacked">
                <div>
                  <strong>{member.full_name ?? member.email}</strong>
                  <span>
                    {member.email}
                    {member.job_title ? ` · ${member.job_title}` : ''}
                  </span>
                  {member.role === 'primary' ? (
                    <span className="cp-muted">{t('Full access as the primary contact.')}</span>
                  ) : (
                    <div className="cp-scope-tags">
                      {member.permissions.length === 0 ? (
                        <span className="cp-muted">{t('No sections granted yet.')}</span>
                      ) : (
                        member.permissions.map((scope) => (
                          <span key={scope} className="cp-tag">
                            {t(GRANTABLE.find((item) => item.scope === scope)?.label ?? scope)}
                          </span>
                        ))
                      )}
                      {member.site_scope?.length ? (
                        <span className="cp-tag cp-tag--site">
                          {t('{count} of {total} sites', {
                            count: member.site_scope.length,
                            total: context.sites.length,
                          })}
                        </span>
                      ) : null}
                    </div>
                  )}
                </div>

                <div className="cp-list__meta">
                  <span className={`cp-chip cp-chip--${member.status === 'active' ? 'live' : member.status}`}>
                    {member.status === 'active'
                      ? t('Active')
                      : member.status === 'invited'
                        ? t('Invited')
                        : t('Disabled')}
                  </span>
                  {member.status === 'invited' ? (
                    <>
                      <span>{t('Expires {date}', { date: formatDate(member.invite_expires_at) })}</span>
                      <button
                        type="button"
                        className="cp-link"
                        disabled={isBusy}
                        onClick={() => member.invite_id && void cancelInvite(member.invite_id)}
                      >
                        {t('Cancel invite')}
                      </button>
                    </>
                  ) : (
                    <span>{t('Last seen {time}', { time: formatRelative(member.last_login_at) })}</span>
                  )}

                  {member.role === 'member' && member.id ? (
                    <>
                      <button
                        type="button"
                        className="cp-link"
                        onClick={() => setEditingId(isEditing ? null : key)}
                      >
                        {isEditing ? t('Done') : t('Change access')}
                      </button>
                      {member.status === 'active' ? (
                        <button
                          type="button"
                          className="cp-link cp-link--danger"
                          disabled={isBusy}
                          onClick={() => void setStatus(member, 'disabled')}
                        >
                          <FiSlash aria-hidden="true" /> {t('Revoke')}
                        </button>
                      ) : member.status === 'disabled' ? (
                        <button
                          type="button"
                          className="cp-link"
                          disabled={isBusy}
                          onClick={() => void setStatus(member, 'active')}
                        >
                          <FiUserCheck aria-hidden="true" /> {t('Restore')}
                        </button>
                      ) : null}
                    </>
                  ) : null}
                </div>

                {isEditing && member.id ? (
                  <div className="cp-perm-grid cp-perm-grid--edit">
                    {grantable.map((item) => (
                      <label key={item.scope} className="cp-perm">
                        <input
                          type="checkbox"
                          checked={member.permissions.includes(item.scope)}
                          disabled={isBusy}
                          onChange={() => {
                            const next = member.permissions.includes(item.scope)
                              ? member.permissions.filter((scope) => scope !== item.scope)
                              : [...member.permissions, item.scope];
                            void savePermissions(member, next);
                          }}
                        />
                        <span><strong>{t(item.label)}</strong></span>
                      </label>
                    ))}
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      </article>
    </div>
  );
}
